import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { UserPlus, Building2, Lock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '../App';


export default function PartnerRegister() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const inviteToken = searchParams.get('token');

  useEffect(() => {
    if (!inviteToken) {
      setTokenValid(false);
    }
  }, [inviteToken]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/partner/register`, {
        invite_token: inviteToken,
        password: password
      });
      
      const { access_token, partner } = response.data;
      
      localStorage.setItem('partner_token', access_token);
      localStorage.setItem('partner_data', JSON.stringify(partner));
      
      toast.success('Registration successful! Welcome to the Partner Portal.');
      navigate('/partner');
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      
      if (message.includes('Invalid') || message.includes('expired')) {
        setTokenValid(false);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 p-4">
        <div className="max-w-md w-full bg-white rounded-sm shadow-md p-8 text-center">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">
            This invitation link is invalid or has expired. If you have already registered, please use the login page. Otherwise, contact Outsiders Travel to receive a new invitation.
          </p>
          <div className="space-y-3">
            <Link to="/partner/login" className="block">
              <Button className="w-full bg-sky-500 hover:bg-sky-600">
                Go to Partner Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative">
      <div className="flex-1 flex items-center justify-center p-8 bg-blue-50">
        <div className="w-full max-w-md">
          
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-32 w-auto object-contain mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className="text-3xl font-bold text-black mb-2" data-testid="partner-register-title">
              Partner Portal
            </h1>
            <p className="text-gray-600 text-lg text-center">
              Complete your registration
            </p>
          </div>

          <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-8">
            <h2 className="text-2xl font-semibold mb-6 text-black flex items-center gap-2" data-testid="partner-register-form-title">
              <Lock className="h-6 w-6 text-sky-500" />
              Set Your Password
            </h2>
            
            <form onSubmit={handleRegister} className="space-y-5" data-testid="partner-register-form">
              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium mb-2 block">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  minLength={6}
                  className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="partner-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-gray-700 font-medium mb-2 block">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="partner-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-medium rounded-sm transition-all"
                data-testid="partner-register-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <UserPlus className="mr-2 h-5 w-5" />
                    Complete Registration
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Already registered?{' '}
                <Link to="/partner/login" className="text-sky-500 hover:text-sky-600 font-medium">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden bg-black"
      >
        <div className="relative z-10 text-white max-w-lg">
          <h2 className="text-4xl font-bold mb-4">Welcome Aboard!</h2>
          <p className="text-blue-200 text-lg leading-relaxed mb-6">
            You're just one step away from accessing the Partner Portal.
          </p>
          <div className="bg-blue-900/50 rounded-sm p-6">
            <h3 className="font-semibold mb-3">What you'll get access to:</h3>
            <ul className="space-y-2 text-blue-100">
              <li>• View all rides assigned to you</li>
              <li>• Track which rides need payment</li>
              <li>• Upload invoices directly</li>
              <li>• Download payment records</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
